import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

import PlannerIcons from '../../components/shared/PlannerIcons';
import { useToast } from '../../contexts/ToastContext';
import { GEMINI_API_KEY } from '../../config/firebase';

export default function ProjectSeating({ project, updateProject }) {
  const toast = useToast();
  const [newTableName, setNewTableName] = useState('');
  const [newTableCapacity, setNewTableCapacity] = useState(8);
  const [newTableShape, setNewTableShape] = useState('round');
  const [newCustomShapeType, setNewCustomShapeType] = useState('rect'); // NEW: custom element shape
  const [newCustomColor, setNewCustomColor] = useState('#1f2937'); // NEW: custom element color
  const [newRotation, setNewRotation] = useState(0); // NEW: table rotation
  const [newScale, setNewScale] = useState(1); // NEW: table size scaling
  const [isAutoSeating, setIsAutoSeating] = useState(false);
  const [viewMode, setViewMode] = useState('cad'); // 'list' | 'cad'
  const [confirmAction, setConfirmAction] = useState(null);
  
  // NEW: Shape Dropdown State
  const [showShapeDropdown, setShowShapeDropdown] = useState(false);
  const shapeDropdownRef = useRef(null);

  // NEW: Layout Shape Dropdown State
  const [showLayoutShapeDropdown, setShowLayoutShapeDropdown] = useState(false);
  const layoutShapeDropdownRef = useRef(null);

  useEffect(() => {
      const handleClickOutside = (e) => {
          if (shapeDropdownRef.current && !shapeDropdownRef.current.contains(e.target)) {
              setShowShapeDropdown(false);
          }
          if (layoutShapeDropdownRef.current && !layoutShapeDropdownRef.current.contains(e.target)) {
              setShowLayoutShapeDropdown(false);
          }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const tableShapes = [
      { id: 'round', name: 'Round', icon: <div className="w-4 h-4 rounded-full border-2 border-gray-500 shrink-0"></div> },
      { id: 'square', name: 'Square', icon: <div className="w-4 h-4 rounded-sm border-2 border-gray-500 shrink-0"></div> },
      { id: 'rect', name: 'Rectangle', icon: <div className="w-6 h-3.5 rounded-sm border-2 border-gray-500 shrink-0"></div> },
      { id: 'long-rect', name: 'Banquet', icon: <div className="w-8 h-3.5 rounded-sm border-2 border-gray-500 shrink-0"></div> },
      { id: 'oval', name: 'Oval', icon: <div className="w-6 h-4 rounded-[50%] border-2 border-gray-500 shrink-0"></div> },
      { id: 'sweetheart', name: 'Sweetheart', icon: <div className="w-5 h-3.5 rounded-t-full rounded-b-[2px] border-2 border-gray-500 shrink-0"></div> },
      { id: 'cocktail', name: 'Cocktail', icon: <div className="w-3 h-3 rounded-full border-2 border-gray-500 shrink-0"></div> },
      { id: 'half-moon', name: 'Half-Moon', icon: <div className="w-6 h-3 rounded-t-full border-2 border-gray-500 shrink-0 border-b-0"></div> },
      { id: 'dance-floor', name: 'Dance Floor', icon: <div className="w-6 h-6 border border-gray-400 grid grid-cols-2 grid-rows-2 shrink-0"><div className="bg-gray-300"></div><div></div><div></div><div className="bg-gray-300"></div></div> },
      { id: 'stage', name: 'Stage', icon: <div className="w-8 h-4 bg-amber-100 border border-amber-300 rounded-sm shrink-0"></div> },
      { id: 'bar', name: 'Bar', icon: <div className="w-6 h-2 bg-gray-800 rounded-sm shrink-0"></div> },
      { id: 'buffet', name: 'Buffet', icon: <div className="w-8 h-3 border border-gray-400 rounded-sm shrink-0 flex items-center justify-between px-0.5"><div className="w-1 h-1 rounded-full bg-gray-300"></div><div className="w-1 h-1 rounded-full bg-gray-300"></div></div> },
      { id: 'dj-booth', name: 'DJ Booth', icon: <div className="w-4 h-3 bg-black rounded-sm shrink-0"></div> },
      { id: 'custom', name: 'Custom', icon: <div className="w-4 h-4 bg-gray-400 rounded-sm text-[8px] text-white flex items-center justify-center font-bold shrink-0">✨</div> },
  ];

  // NEW: State for Real-World Room Dimensions (ft) and conversion constants
  const initialRoomDim = project.roomDimensions || 
      (project.canvasSize ? { w: Math.round(project.canvasSize.w / 30) || 60, h: Math.round(project.canvasSize.h / 30) || 40 } : { w: 60, h: 40 });
  const [roomDim, setRoomDim] = useState(initialRoomDim);
  const PX_PER_FT = 30; // 30 pixels per real-world foot
  const canvasW = roomDim.w * PX_PER_FT;
  const canvasH = roomDim.h * PX_PER_FT;

  // NEW: Base real-world dimensions for elements (in feet)
  const shapeDimensions = {
      'round': { w: 5, h: 5 },
      'square': { w: 5, h: 5 },
      'rect': { w: 6, h: 2.5 },
      'long-rect': { w: 8, h: 2.5 },
      'oval': { w: 8, h: 4 },
      'sweetheart': { w: 4, h: 2.5 },
      'cocktail': { w: 2.5, h: 2.5 },
      'half-moon': { w: 5, h: 2.5 },
      'dance-floor': { w: 20, h: 20 },
      'stage': { w: 20, h: 10 },
      'bar': { w: 10, h: 3 },
      'buffet': { w: 16, h: 3 },
      'dj-booth': { w: 6, h: 4 },
      'custom': { w: 6, h: 6 }
  };

  // NEW: Edit Mode State
  const [isEditing, setIsEditing] = useState(false);

  // State for editing tables
  const [editingTableId, setEditingTableId] = useState(null);

  // NEW: Mobile-friendly Tap-to-Seat state
  const [selectedGuestId, setSelectedGuestId] = useState(null);
  const [activeTableTooltip, setActiveTableTooltip] = useState(null);
  
  // NEW: Layout State
  const defaultLayouts = [{ id: 'default', name: 'Main Layout' }];
  const layouts = project.seatingLayouts || defaultLayouts;
  const [activeLayoutId, setActiveLayoutId] = useState(layouts[0]?.id || 'default');
  const [isAddingLayout, setIsAddingLayout] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState('');
  
  // NEW: Edit Layout & Position States
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const [editLayoutName, setEditLayoutName] = useState('');
  const [newXPos, setNewXPos] = useState(50);
  const [newYPos, setNewYPos] = useState(50);
  
  // NEW: Auto-Add tables during layout creation
  const [newLayoutTableCount, setNewLayoutTableCount] = useState(0);
  const [newLayoutTableCapacity, setNewLayoutTableCapacity] = useState(8);
  const [newLayoutTableShape, setNewLayoutTableShape] = useState('round');
  
  // Handle Add Layout
  const handleAddLayout = (e) => {
      e.preventDefault();
      if (!newLayoutName.trim()) return;
      const newId = Date.now().toString();
      const newLayouts = [...layouts, { id: newId, name: newLayoutName.trim() }];
      
      let updatedTables = [...(project.tables || [])];
      
      // Generate initial tables if requested
      if (newLayoutTableCount > 0) {
          const generatedTables = Array.from({ length: newLayoutTableCount }).map((_, i) => ({
              id: Date.now().toString() + '_' + i,
              name: `Table ${i + 1}`,
              capacity: newLayoutTableCapacity,
              shape: newLayoutTableShape,
              customShapeType: null,
              color: null,
              layoutId: newId,
              x: 20 + ((i % 5) * 15), // Spread out on the canvas
              y: 20 + (Math.floor(i / 5) * 15)
          }));
          updatedTables = [...updatedTables, ...generatedTables];
      }

      saveHistory(project.tables || []); // Save history before modifying
      
      updateProject(project.id, { 
          seatingLayouts: newLayouts,
          tables: updatedTables
      });
      
      setActiveLayoutId(newId);
      setIsAddingLayout(false);
      setNewLayoutName('');
      setNewLayoutTableCount(0);
      setNewLayoutTableCapacity(8);
      setNewLayoutTableShape('round');
  };

  // NEW: Layout Edit & Delete
  const handleEditLayoutClick = () => {
      const currentLayout = layouts.find(l => l.id === activeLayoutId);
      if (currentLayout) {
          setEditLayoutName(currentLayout.name);
          setIsEditingLayout(true);
      }
  };

  const handleUpdateLayout = (e) => {
      e.preventDefault();
      if (!editLayoutName.trim()) return;
      const updatedLayouts = layouts.map(l => l.id === activeLayoutId ? { ...l, name: editLayoutName.trim() } : l);
      updateProject(project.id, { seatingLayouts: updatedLayouts });
      setIsEditingLayout(false);
  };

  const handleDeleteLayoutClick = () => {
      if (layouts.length <= 1) {
          toast.error("You must have at least one layout.");
          return;
      }
      setConfirmAction({ type: 'deleteLayout' });
  };

  const confirmDeleteLayout = () => {
      const updatedLayouts = layouts.filter(l => l.id !== activeLayoutId);
      const updatedTables = allTables.filter(t => (t.layoutId || 'default') !== activeLayoutId);
      
      saveHistory(allTables); // Save history before modifying
      updateProject(project.id, { 
          seatingLayouts: updatedLayouts,
          tables: updatedTables
      });
      setActiveLayoutId(updatedLayouts[0]?.id || 'default');
      setConfirmAction(null);
      toast.success("Layout deleted successfully.");
  };

  const allTables = project.tables || [];
  const tables = allTables.filter(t => (t.layoutId || 'default') === activeLayoutId);
  const guests = project.guests || [];

  // NEW: Smart Auto-Seater Algorithm
  const handleAutoSeatGuests = () => {
      setIsAutoSeating(true);
      
      setTimeout(() => {
          const currentGuests = [...(project.guests || [])];
          const availableTables = tables.filter(t => t.shape !== 'custom').map(t => {
              const seatedCount = currentGuests.filter(g => g.tableId === t.id).length;
              return { ...t, remaining: t.capacity - seatedCount };
          });

          // Only seat "Attending" guests who do NOT have a table
          const unseated = currentGuests.filter(g => g.rsvp === 'Attending' && !g.tableId);
          
          if (unseated.length === 0) {
              toast.error("All attending guests are already seated!");
              setIsAutoSeating(false);
              return;
          }

          // Group unseated guests by their relationship group (Family, Friends, etc.)
          const groups = {};
          unseated.forEach(g => {
              const groupName = g.group || 'Other';
              if (!groups[groupName]) groups[groupName] = [];
              groups[groupName].push(g);
          });

          let newlySeatedCount = 0;

          // Greedy packing: keep groups together as much as possible
          Object.keys(groups).forEach(groupName => {
              const groupGuests = groups[groupName];
              
              for (let guest of groupGuests) {
                  // Sort tables by most remaining space first
                  availableTables.sort((a,b) => b.remaining - a.remaining);
                  const targetTable = availableTables.find(t => t.remaining > 0);
                  
                  if (targetTable) {
                      guest.tableId = targetTable.id;
                      guest.table = targetTable.name;
                      targetTable.remaining -= 1;
                      newlySeatedCount++;
                  }
              }
          });

          if (newlySeatedCount > 0) {
              const finalGuests = currentGuests.map(g => {
                  const updatedMatch = unseated.find(u => u.id === g.id);
                  return updatedMatch ? updatedMatch : g;
              });
              updateProject(project.id, { guests: finalGuests });
              toast.success(`Successfully auto-seated ${newlySeatedCount} guests based on their groups!`);
          } else {
              toast.error("No available seats left! Please add more tables.");
          }
          setIsAutoSeating(false);
      }, 600); // UI feedback delay
  };

  const [localTables, setLocalTables] = useState(project.tables || []);
  const [draggingTable, setDraggingTable] = useState(null);
  
  // NEW: Direct Scaling State
  const [scalingTableId, setScalingTableId] = useState(null);
  const [scaleStart, setScaleStart] = useState({ x: 0, y: 0, initialScale: 1 });
  
  // State for alignment guides while dragging in CAD view
  const [guides, setGuides] = useState({ x: null, y: null });

  // NEW: State for Undo/Redo functionality
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // AI Layout Helper State
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  
  // Pan, Zoom, and Fullscreen States
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // NEW: Measurement & Grid Tools
  const [activeTool, setActiveTool] = useState('select'); // 'select' | 'measure'
  const [measureStart, setMeasureStart] = useState(null);
  const [measureEnd, setMeasureEnd] = useState(null);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(false);

  const canvasRef = useRef(null);
  const outerRef = useRef(null); // Ref for capturing wheel events

  // NEW: Helper to keep pan strictly within canvas boundaries using calculated pixel width/height
  const clampPan = (newX, newY, currentZoom) => {
      if (!outerRef.current) return { x: newX, y: newY };
      const outerRect = outerRef.current.getBoundingClientRect();
      
      // Calculate max allowed pan based on canvas scaled size vs container size.
      // Add 50px buffer so the edges aren't completely flush with the boundary.
      const boundX = Math.max(0, (canvasW * currentZoom - outerRect.width) / 2 + 50);
      const boundY = Math.max(0, (canvasH * currentZoom - outerRect.height) / 2 + 50);
      
      return {
          x: Math.min(Math.max(newX, -boundX), boundX),
          y: Math.min(Math.max(newY, -boundY), boundY)
      };
  };

  // Handle Wheel Events for Zoom/Pan
  useEffect(() => {
      if (viewMode !== 'cad') return;
      const outer = outerRef.current;
      if (!outer) return;
      
      const wheelListener = (e) => {
          e.preventDefault(); // Stop page scrolling
          if (e.ctrlKey || e.metaKey) {
              const zoomChange = e.deltaY * -0.01;
              setZoom(prev => {
                  const nextZoom = Math.min(Math.max(0.1, prev + zoomChange), 4);
                  // Clamp pan using the new target zoom
                  setPan(p => clampPan(p.x, p.y, nextZoom));
                  return nextZoom;
              });
          } else {
              setPan(prev => clampPan(prev.x - e.deltaX, prev.y - e.deltaY, zoom));
          }
      };
      
      outer.addEventListener('wheel', wheelListener, { passive: false });
      return () => {
          outer.removeEventListener('wheel', wheelListener);
      };
  }, [viewMode, zoom, canvasW, canvasH]);

  // NEW: Save History Helper
  const saveHistory = (currentTables) => {
      setHistory(prev => [...prev, currentTables].slice(-20)); // Keep last 20 states
      setRedoStack([]); // Clear redo stack on new action
  };

  const handleUndo = () => {
      if (history.length === 0) return;
      const prevTables = history[history.length - 1];
      setRedoStack(prev => [allTables, ...prev]);
      setHistory(prev => prev.slice(0, -1));
      updateProject(project.id, { tables: prevTables });
  };

  const handleRedo = () => {
      if (redoStack.length === 0) return;
      const nextTables = redoStack[0];
      setHistory(prev => [...prev, allTables]);
      setRedoStack(prev => prev.slice(1));
      updateProject(project.id, { tables: nextTables });
  };

  // NEW: Keyboard Shortcuts for Undo/Redo
  useEffect(() => {
      if (viewMode !== 'cad' || !isEditing) return;

      const handleKeyDown = (e) => {
          // Ignore if user is typing in an input, textarea, or select field
          if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;

          if (e.ctrlKey || e.metaKey) {
              if (e.key.toLowerCase() === 'z') {
                  e.preventDefault();
                  if (e.shiftKey) {
                      handleRedo();
                  } else {
                      handleUndo();
                  }
              } else if (e.key.toLowerCase() === 'y') {
                  e.preventDefault();
                  handleRedo();
              }
          }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, isEditing, history, redoStack, allTables, project.id, updateProject]);

  useEffect(() => {
      if (!draggingTable) {
          const currentAllTables = project.tables || [];
          setLocalTables(currentAllTables.filter(t => (t.layoutId || 'default') === activeLayoutId));
      }
  }, [project.tables, draggingTable, activeLayoutId]);

  const handleTableSubmit = (e) => {
    e.preventDefault();
    if (!newTableName) return;
    
    saveHistory(allTables); // Save history before modifying

    if (editingTableId) {
        const updatedTables = allTables.map(t => 
            t.id === editingTableId 
                ? { 
                    ...t, 
                    name: newTableName, 
                    capacity: newTableShape === 'custom' ? 0 : parseInt(newTableCapacity), 
                    shape: newTableShape,
                    customShapeType: newTableShape === 'custom' ? newCustomShapeType : null,
                    color: newTableShape === 'custom' ? newCustomColor : null,
                    x: parseFloat(newXPos) || 50,
                    y: parseFloat(newYPos) || 50,
                    rotation: parseFloat(newRotation) || 0,
                    scale: parseFloat(newScale) || 1
                  } 
                : t
        );
        updateProject(project.id, { tables: updatedTables });
        setEditingTableId(null);
    } else {
        const newTable = { 
            id: Date.now().toString(), 
            name: newTableName, 
            capacity: newTableShape === 'custom' ? 0 : parseInt(newTableCapacity),
            shape: newTableShape,
            customShapeType: newTableShape === 'custom' ? newCustomShapeType : null,
            color: newTableShape === 'custom' ? newCustomColor : null,
            layoutId: activeLayoutId,
            x: parseFloat(newXPos) || 50, 
            y: parseFloat(newYPos) || 50,
            rotation: parseFloat(newRotation) || 0,
            scale: parseFloat(newScale) || 1
        };
        updateProject(project.id, { tables: [...allTables, newTable] });
    }
    
    setNewTableName('');
    setNewTableCapacity(8);
    setNewTableShape('round');
    setNewCustomShapeType('rect');
    setNewCustomColor('#1f2937');
    setNewXPos(50);
    setNewYPos(50);
    setNewRotation(0);
    setNewScale(1);
  };

  const handleEditTableClick = (table) => {
      setEditingTableId(table.id);
      setNewTableName(table.name);
      setNewTableCapacity(table.capacity);
      setNewTableShape(table.shape || 'round');
      setNewCustomShapeType(table.customShapeType || 'rect');
      setNewCustomColor(table.color || '#1f2937');
      setNewXPos(table.x || 50);
      setNewYPos(table.y || 50);
      setNewRotation(table.rotation || 0);
      setNewScale(table.scale || 1);
  };

  const cancelEdit = () => {
      setEditingTableId(null);
      setNewTableName('');
      setNewTableCapacity(8);
      setNewTableShape('round');
      setNewCustomShapeType('rect');
      setNewCustomColor('#1f2937');
      setNewXPos(50);
      setNewYPos(50);
      setNewRotation(0);
      setNewScale(1);
  };

  // NEW: Auto-Generate Floor Plan
  const handleAutoGenerateFloorPlanClick = () => {
      setConfirmAction({ type: 'autoGenerate' });
  };

  const executeAutoGenerateFloorPlan = () => {
      saveHistory(allTables); // Save history before modifying
      
      const guestCount = guests.length > 0 ? guests.length : 100;
      const cap = ['custom', 'dance-floor', 'bar', 'stage', 'buffet', 'dj-booth'].includes(newTableShape) ? 8 : (parseInt(newTableCapacity) || 8);
      const tShape = ['custom', 'dance-floor', 'bar', 'stage', 'buffet', 'dj-booth'].includes(newTableShape) ? 'round' : newTableShape;
      const numTables = Math.ceil(guestCount / cap);
      
      const newTables = [...allTables.filter(t => t.layoutId !== activeLayoutId)];
      
      newTables.push({
          id: `auto-sweetheart-${Date.now()}`,
          name: "Sweetheart Table",
          capacity: 2,
          shape: "sweetheart",
          layoutId: activeLayoutId,
          x: 50,
          y: 15,
          rotation: 0,
          scale: 1.2
      });
      
      newTables.push({
          id: `auto-df-${Date.now()}`,
          name: "Dance Floor",
          capacity: 0,
          shape: "dance-floor",
          layoutId: activeLayoutId,
          x: 50,
          y: 40,
          rotation: 0,
          scale: 1.2
      });

      newTables.push({
          id: `auto-dj-${Date.now()}`,
          name: "DJ",
          capacity: 0,
          shape: "dj-booth",
          layoutId: activeLayoutId,
          x: 50,
          y: 55,
          rotation: 0,
          scale: 0.8
      });

      const cols = Math.ceil(Math.sqrt(numTables));
      const rows = Math.ceil(numTables / cols);
      
      const startX = 15;
      const endX = 85;
      const startY = 70;
      const endY = 90;
      
      let count = 0;
      for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
              if (count >= numTables) break;
              
              const px = cols > 1 ? startX + (c * ((endX - startX) / (cols - 1))) : 50;
              const py = rows > 1 ? startY + (r * ((endY - startY) / (rows - 1))) : startY;
              
              newTables.push({
                  id: `auto-t-${count}-${Date.now()}`,
                  name: `Table ${count + 1}`,
                  capacity: cap,
                  shape: tShape,
                  layoutId: activeLayoutId,
                  x: px,
                  y: py,
                  rotation: 0,
                  scale: 1
              });
              count++;
          }
      }
      
      updateProject(project.id, { tables: newTables });
      setConfirmAction(null);
      toast.success("Floor plan auto-generated!");
  };

  const removeTable = (tableId) => {
    saveHistory(allTables); // Save history before modifying
    const updatedGuests = guests.map(g => g.tableId === tableId ? { ...g, tableId: null, table: '' } : g);
    updateProject(project.id, { tables: allTables.filter(t => t.id !== tableId), guests: updatedGuests });
  };

  const duplicateTable = (table) => {
      saveHistory(allTables);
      const newTable = {
          ...table,
          id: Date.now().toString(),
          name: `${table.name} (Copy)`,
          x: Math.min((table.x || 50) + 5, 95), 
          y: Math.min((table.y || 50) + 5, 95)
      };
      updateProject(project.id, { tables: [...allTables, newTable] });
      toast.success("Table duplicated!");
  };

  const quickRotate = (table) => {
      saveHistory(allTables);
      const newRotation = ((parseFloat(table.rotation) || 0) + 45) % 360;
      const updatedTables = allTables.map(t => t.id === table.id ? { ...t, rotation: newRotation } : t);
      updateProject(project.id, { tables: updatedTables });
      setLocalTables(prev => prev.map(t => t.id === table.id ? { ...t, rotation: newRotation } : t));
      if (editingTableId === table.id) setNewRotation(newRotation);
  };

  const bringToFront = (tableId) => {
      saveHistory(allTables);
      const tableIndex = allTables.findIndex(t => t.id === tableId);
      if (tableIndex > -1) {
          const newTables = [...allTables];
          const [table] = newTables.splice(tableIndex, 1);
          newTables.push(table);
          updateProject(project.id, { tables: newTables });
      }
  };

  const assignGuest = (guestId, tableId) => {
    const table = tables.find(t => t.id === tableId);
    const updatedGuests = guests.map(g => g.id === guestId ? { ...g, tableId: tableId, table: table ? table.name : '' } : g);
    updateProject(project.id, { guests: updatedGuests });
  };

  const unassignedGuests = guests.filter(g => !g.tableId && g.rsvp === 'Attending');

  const handleAutoSeat = () => {
    setIsAutoSeating(true);
    setTimeout(() => {
      let updatedGuests = [...guests];
      let currentTables = tables.map(t => ({
        ...t,
        remaining: t.capacity - updatedGuests.filter(g => g.tableId === t.id).length
      }));

      const unassigned = updatedGuests.filter(g => !g.tableId && g.rsvp === 'Attending');
      
      const grouped = unassigned.reduce((acc, g) => {
        const key = g.group || 'Other';
        if (!acc[key]) acc[key] = [];
        acc[key].push(g);
        return acc;
      }, {});

      const sortedGroups = Object.values(grouped).sort((a, b) => b.length - a.length);

      sortedGroups.forEach(groupGuests => {
        groupGuests.forEach(guest => {
          let bestTable = currentTables.find(t => t.remaining > 0 && updatedGuests.some(g => g.tableId === t.id && g.group === guest.group));
          if (!bestTable) {
            bestTable = [...currentTables].sort((a, b) => b.remaining - a.remaining).find(t => t.remaining > 0);
          }
          if (bestTable) {
            const guestIndex = updatedGuests.findIndex(g => g.id === guest.id);
            updatedGuests[guestIndex] = { ...guest, tableId: bestTable.id, table: bestTable.name };
            bestTable.remaining -= 1;
          }
        });
      });

      updateProject(project.id, { guests: updatedGuests });
      setIsAutoSeating(false);
    }, 800);
  };

  const handleAiGenerateLayout = async () => {
      if (!aiPrompt.trim()) return;
      setIsAiGenerating(true);

      try {
          const prompt = `You are an AI floor plan assistant for an event planner.
          User request: "${aiPrompt}"

          Extract the requested tables and layout elements.
          Return strictly a JSON array of objects. Do NOT wrap in markdown block quotes, just return the raw JSON array.
          Each object must have these exact keys:
          - "name": string (e.g. "Round Table 1", "Dance Floor", "DJ Booth")
          - "capacity": number (seats available. Use 0 for custom elements like stages, bars, dance floors, etc.)
          - "shape": string (MUST be exactly one of: "round", "rect", "square", or "custom". Use "custom" for any non-seating element like a stage, dance floor, photo booth, etc.)
          - "x": number (Generate a random integer between 25 and 75 to spread them out on the canvas)
          - "y": number (Generate a random integer between 25 and 75 to spread them out on the canvas)`;

          const payload = {
              contents: [{ role: "user", parts: [{ text: prompt }] }],
              generationConfig: { responseMimeType: "application/json" }
          };
          
          const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });

          const result = await response.json();
          if (result.candidates?.[0]) {
              const text = result.candidates[0].content.parts[0].text;
              const jsonStr = text.replace(/```json/gi, '').replace(/```/g, '').trim();
              const generatedItems = JSON.parse(jsonStr);

              const newItemsWithIds = generatedItems.map((item, idx) => ({
                  ...item,
                  id: Date.now().toString() + idx,
                  layoutId: activeLayoutId
              }));

              saveHistory(allTables); // Save history before modifying
              updateProject(project.id, { tables: [...allTables, ...newItemsWithIds] });
              setShowAiModal(false);
              setAiPrompt('');
          }
      } catch (err) {
          console.error("AI Layout Generation Error:", err);
          toast.error("Failed to generate layout. Please try again.");
      } finally {
          setIsAiGenerating(false);
      }
  };

  // --- CAD Interaction Handlers ---
  const handleTableDragStart = (e, tableId) => {
      e.stopPropagation(); // Prevent canvas panning trigger
      setDraggingTable(tableId);
      
      const table = localTables.find(t => t.id === tableId);
      if (table) {
          handleEditTableClick(table);
      }
  };

  const handleScaleStart = (e, table) => {
      e.stopPropagation();
      e.preventDefault();
      const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
      const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
      if (clientX === undefined || clientY === undefined) return;

      setScalingTableId(table.id);
      setScaleStart({ x: clientX, y: clientY, initialScale: table.scale || 1 });
      handleEditTableClick(table);
  };

  // Background Drag for Panning
  const handleCanvasMouseDownBg = (e) => {
      setActiveTableTooltip(null); // Clear tooltips on bg tap
      
      // NEW: Measurement Tool Logic
      if (activeTool === 'measure' && canvasRef.current) {
          const rect = canvasRef.current.getBoundingClientRect();
          const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
          const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
          const x = ((clientX - rect.left) / rect.width) * 100;
          const y = ((clientY - rect.top) / rect.height) * 100;
          setMeasureStart({ x, y });
          setMeasureEnd({ x, y });
          setIsMeasuring(true);
          return; // Prevent panning
      }

      if (e.target.id === 'canvas-bg') {
          setIsPanning(true);
          const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
          const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
          setLastMousePos({ x: clientX, y: clientY });
      }
  };

  const handleCanvasMouseMove = (e) => {
      const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
      const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
      if (clientX === undefined || clientY === undefined) return;

      // NEW: Measurement Tool Dragging
      if (isMeasuring && canvasRef.current) {
          const rect = canvasRef.current.getBoundingClientRect();
          let x = ((clientX - rect.left) / rect.width) * 100;
          let y = ((clientY - rect.top) / rect.height) * 100;
          // Bound to 0-100
          x = Math.max(0, Math.min(100, x));
          y = Math.max(0, Math.min(100, y));
          setMeasureEnd({ x, y });
          return;
      }

      if (scalingTableId && canvasRef.current) {
          const dx = clientX - scaleStart.x;
          const dy = clientY - scaleStart.y;
          // Moving down/right increases scale
          const distance = dx + dy;
          const scaleChange = distance / (150 * zoom);
          const calculatedScale = Math.max(0.2, Math.min(15, scaleStart.initialScale + scaleChange));
          
          setLocalTables(prev => prev.map(t => t.id === scalingTableId ? { ...t, scale: calculatedScale } : t));
          if (editingTableId === scalingTableId) {
              setNewScale(calculatedScale);
          }
          return;
      }

      if (isPanning) {
          const dx = clientX - lastMousePos.x;
          const dy = clientY - lastMousePos.y;
          setPan(prev => clampPan(prev.x + dx, prev.y + dy, zoom));
          setLastMousePos({ x: clientX, y: clientY });
          return;
      }

      if (!draggingTable || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const SNAP_THRESHOLD = 0.5; // Optimized for larger 3000px canvas
      
      let newX = ((clientX - rect.left) / rect.width) * 100;
      let newY = ((clientY - rect.top) / rect.height) * 100;

      newX = Math.max(0, Math.min(100, newX));
      newY = Math.max(0, Math.min(100, newY));

      let guideX = null;
      let guideY = null;

      // NEW: Grid Snapping
      if (snapToGrid) {
          newX = Math.round(newX / 2) * 2; // Snap to 2% grid
          newY = Math.round(newY / 2) * 2;
      } else {
          localTables.forEach(t => {
              if (t.id === draggingTable) return;
              if (Math.abs(newX - t.x) < SNAP_THRESHOLD) { newX = t.x; guideX = t.x; }
              if (Math.abs(newY - t.y) < SNAP_THRESHOLD) { newY = t.y; guideY = t.y; }
          });

          if (Math.abs(newX - 50) < SNAP_THRESHOLD) { newX = 50; guideX = 50; }
          if (Math.abs(newY - 50) < SNAP_THRESHOLD) { newY = 50; guideY = 50; }
      }

      setGuides({ x: guideX, y: guideY });
      setLocalTables(prev => prev.map(t => t.id === draggingTable ? { ...t, x: newX, y: newY } : t));
  };

  const handleCanvasMouseUp = () => {
      // NEW: Stop measuring
      if (isMeasuring) {
          setIsMeasuring(false);
          return;
      }
      if (isPanning) setIsPanning(false);
      
      if (scalingTableId) {
          saveHistory(allTables); 
          const updatedTables = allTables.map(t => {
              const localMatch = localTables.find(lt => lt.id === t.id);
              return localMatch ? localMatch : t;
          });
          updateProject(project.id, { tables: updatedTables });
          setScalingTableId(null);
      }

      if (draggingTable) {
          saveHistory(allTables); // Save history before saving new drag positions
          const updatedTables = allTables.map(t => {
              const localMatch = localTables.find(lt => lt.id === t.id);
              return localMatch ? localMatch : t;
          });
          updateProject(project.id, { tables: updatedTables });
          
          if (editingTableId === draggingTable) {
              const localMatch = localTables.find(lt => lt.id === draggingTable);
              if (localMatch) {
                  setNewXPos(localMatch.x);
                  setNewYPos(localMatch.y);
              }
          }

          setDraggingTable(null);
          setGuides({ x: null, y: null });
      }
  };

  const handleGuestDragStart = (e, guestId) => {
      e.dataTransfer.setData('guestId', guestId);
  };

  const handleTableDrop = (e, tableId) => {
      e.preventDefault();
      const guestId = e.dataTransfer.getData('guestId');
      if (guestId) {
          assignGuest(guestId, tableId);
      }
  };

  // NEW: Handler for dropping a guest back into the sidebar to unassign them
  const handleSidebarDrop = (e) => {
      e.preventDefault();
      const guestId = e.dataTransfer.getData('guestId');
      if (guestId) {
          assignGuest(guestId, null);
      }
  };

  // Toolbar Actions
  const handleZoomIn = () => setZoom(prev => {
      const nextZoom = Math.min(prev + 0.2, 3);
      setPan(p => clampPan(p.x, p.y, nextZoom));
      return nextZoom;
  });
  
  const handleZoomOut = () => setZoom(prev => {
      const nextZoom = Math.max(prev - 0.2, 0.1);
      setPan(p => clampPan(p.x, p.y, nextZoom));
      return nextZoom;
  });
  
  const handleFit = () => { 
      if (outerRef.current) {
          const outerRect = outerRef.current.getBoundingClientRect();
          const padding = 80; // 40px padding on each side
          const scaleX = (outerRect.width - padding) / canvasW;
          const scaleY = (outerRect.height - padding) / canvasH;
          
          // Calculate the exact zoom needed to fit the whole canvas
          const newZoom = Math.max(0.1, Math.min(scaleX, scaleY, 1.5)); 
          
          setZoom(newZoom);
          // 0,0 puts the canvas exactly in the center of the outer container
          setPan({ x: 0, y: 0 }); 
      } else {
          setZoom(1); 
          setPan({ x: 0, y: 0 }); 
      }
  };
  
  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const activeBaseDim = { ...(shapeDimensions[newTableShape] || shapeDimensions['round']) };
  if (newTableShape === 'custom' && newCustomShapeType === 'pill') {
      activeBaseDim.w = 8;
      activeBaseDim.h = 3;
  }
  const activeW = Math.round(activeBaseDim.w * (newScale || 1) * 10) / 10;
  const activeH = Math.round(activeBaseDim.h * (newScale || 1) * 10) / 10;

  const seatingContent = (
    <>
      {confirmAction && (
          <div className="fixed inset-0 bg-black/60 z-[500] flex items-center justify-center p-4 animate-fade-in">
              <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm">
                  <h3 className="text-xl font-bold mb-4 text-gray-900">
                      {confirmAction.type === 'deleteLayout' ? 'Delete Layout?' : 'Auto-Generate Floor Plan?'}
                  </h3>
                  <p className="mb-6 text-gray-600 text-sm">
                      {confirmAction.type === 'deleteLayout'
                          ? "Are you sure you want to permanently delete this layout and all its elements? This action cannot be undone."
                          : "This will replace your current layout with an AI-generated floor plan based on your total guests. Continue?"}
                  </p>
                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                      <button onClick={() => setConfirmAction(null)} className="px-5 py-2 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                      <button
                          onClick={confirmAction.type === 'deleteLayout' ? confirmDeleteLayout : executeAutoGenerateFloorPlan}
                          className={`px-5 py-2 text-white rounded-lg font-bold shadow-md transition ${confirmAction.type === 'deleteLayout' ? 'bg-red-600 hover:bg-red-700' : 'bg-pink-600 hover:bg-pink-700'}`}
                      >
                          {confirmAction.type === 'deleteLayout' ? 'Delete' : 'Generate'}
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* AI Layout Helper Modal */}
      {showAiModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-[500] flex items-center justify-center p-4 animate-fade-in">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2 text-gray-900">
                      <span>🪄</span> AI Layout Generator
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                      Describe the tables and elements you need, and the AI will add them to your floor plan instantly.
                  </p>
                  <textarea 
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="e.g., We need 5 round tables that seat 8, 6 square ones that seat 10, a stage, a dance floor, and a photo booth."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none h-32 resize-none text-sm"
                  />
                  <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
                      <button onClick={() => setShowAiModal(false)} className="px-5 py-2 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                      <button 
                          onClick={handleAiGenerateLayout} 
                          disabled={isAiGenerating || !aiPrompt.trim()}
                          className="px-5 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg font-bold hover:from-purple-700 hover:to-pink-600 transition shadow-md flex items-center gap-2 disabled:opacity-75"
                      >
                          {isAiGenerating ? (
                              <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> Generating...</>
                          ) : (
                              <>Generate Elements</>
                          )}
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* Guest Sidebar */}
      {unassignedGuests.length > 0 && (
          <div 
            className={`w-full bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden flex-shrink-0 transition-all duration-300 lg:w-72 ${isFullscreen ? 'h-48 lg:h-full flex-shrink-0' : 'h-[350px] lg:h-auto lg:max-h-full'}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleSidebarDrop}
          >
            <div className="p-3 md:p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center lg:block">
              <h3 className="font-bold text-gray-900">Needs Seating</h3>
              <p className="text-xs font-bold text-pink-600 mt-0 lg:mt-1">{unassignedGuests.length} guests</p>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2 lg:space-y-3 custom-scrollbar">
              {tables.length > 0 && (
                <button 
                  onClick={handleAutoSeat}
                  disabled={isAutoSeating}
                  className="w-full mb-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-bold py-2.5 rounded-lg shadow-sm hover:from-purple-700 hover:to-pink-600 transition flex items-center justify-center gap-2 disabled:opacity-75"
                >
                  {isAutoSeating ? (
                    <><span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span> Optimizing...</>
                  ) : (
                    <>✨ Smart Auto-Seat</>
                  )}
                </button>
              )}
              <p className="text-[10px] text-pink-700 font-bold bg-pink-50 border border-pink-100 p-1.5 rounded text-center mb-2">
                  💡 Tap a guest, then tap a table to seat them!
              </p>
              {unassignedGuests.map(guest => (
                <div 
                    key={guest.id} 
                    draggable="true"
                    onDragStart={(e) => handleGuestDragStart(e, guest.id)}
                    onClick={() => {
                        if (selectedGuestId === guest.id) setSelectedGuestId(null);
                        else setSelectedGuestId(guest.id);
                    }}
                    className={`p-2 md:p-3 bg-white rounded-lg border flex flex-col gap-2 shadow-sm cursor-pointer transition-all ${
                        selectedGuestId === guest.id 
                        ? 'border-pink-500 ring-2 ring-pink-400 bg-pink-50 transform scale-[1.02]' 
                        : 'border-gray-200 hover:border-pink-300'
                    }`}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-sm text-gray-900">{guest.name}</span>
                    {guest.meal && <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-bold">{guest.meal}</span>}
                  </div>
                  <select 
                    value="" 
                    onChange={(e) => assignGuest(guest.id, e.target.value)}
                    className="w-full text-xs p-1.5 border border-gray-300 rounded outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 font-medium text-gray-700 bg-white"
                  >
                    <option value="" disabled>Assign to table...</option>
                    {tables.map(t => {
                      const seatedCount = guests.filter(g => g.tableId === t.id).length;
                      return (
                        <option key={t.id} value={t.id} disabled={seatedCount >= t.capacity}>
                          {t.name} ({seatedCount}/{t.capacity})
                        </option>
                      )
                    })}
                  </select>
                </div>
              ))}
            </div>
          </div>
      )}

      {/* Main Workspace */}
      <div className={`flex-1 flex flex-col gap-4 min-h-0 ${!isFullscreen ? 'min-h-[600px] lg:min-h-0' : ''}`}>
        
        {/* NEW: Mobile Editing Warning */}
        {isEditing && viewMode === 'cad' && (
            <div className="lg:hidden bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-[10px] md:text-xs font-bold border border-blue-200 flex-shrink-0 shadow-sm">
                💡 Tip: Editing the floor plan is best experienced on a tablet or desktop screen.
            </div>
        )}

        {/* Unified Tool Bar */}
        <div className="flex flex-col xl:flex-row gap-3 items-start xl:items-center justify-between bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
            {/* NEW: Layout Selector */}
            <div className="flex items-center gap-2 border-b xl:border-b-0 xl:border-r border-gray-200 pb-3 xl:pb-0 xl:pr-3 w-full xl:w-auto shrink-0">
                {isAddingLayout ? (
                    <form onSubmit={handleAddLayout} className="flex flex-wrap items-center gap-2 bg-gray-50 p-1.5 rounded-lg border border-gray-200">
                        <input 
                            type="text" 
                            autoFocus
                            value={newLayoutName} 
                            onChange={e => setNewLayoutName(e.target.value)} 
                            placeholder="Layout name..." 
                            className="w-32 p-1.5 border border-gray-300 rounded-md text-xs font-medium outline-none focus:ring-1 focus:ring-pink-500"
                            required
                        />
                        <div className="flex items-center gap-1 border-l border-gray-300 pl-2">
                            <span className="text-xs text-gray-500 font-medium">Add:</span>
                            <input 
                                type="number" 
                                min="0"
                                value={newLayoutTableCount}
                                onChange={e => setNewLayoutTableCount(parseInt(e.target.value) || 0)}
                                placeholder="Qty"
                                className="w-14 p-1.5 border border-gray-300 rounded-md text-xs"
                                title="Number of tables"
                            />
                            <span className="text-xs text-gray-500">tables,</span>
                            <input 
                                type="number" 
                                min="1"
                                value={newLayoutTableCapacity}
                                onChange={e => setNewLayoutTableCapacity(parseInt(e.target.value) || 1)}
                                placeholder="Seats"
                                className="w-14 p-1.5 border border-gray-300 rounded-md text-xs"
                                title="Seats per table"
                            />
                            <span className="text-xs text-gray-500">seats,</span>
                            <div className="relative" ref={layoutShapeDropdownRef}>
                                <button 
                                    type="button" 
                                    onClick={() => setShowLayoutShapeDropdown(!showLayoutShapeDropdown)}
                                    className="w-32 p-1.5 border border-gray-300 rounded-md text-xs font-medium focus:outline-none focus:ring-1 focus:ring-pink-500 bg-white flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-1.5 truncate">
                                        {tableShapes.find(s => s.id === newLayoutTableShape)?.icon}
                                        <span className="truncate">{tableShapes.find(s => s.id === newLayoutTableShape)?.name}</span>
                                    </div>
                                    <svg className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${showLayoutShapeDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                </button>
                                {showLayoutShapeDropdown && (
                                    <div className="absolute z-[100] top-full mt-1 right-0 w-44 bg-white border border-gray-200 rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto custom-scrollbar">
                                        {tableShapes.map(shape => (
                                            <div 
                                                key={shape.id} 
                                                onClick={() => { setNewLayoutTableShape(shape.id); setShowLayoutShapeDropdown(false); }}
                                                className={`flex items-center gap-3 px-3 py-2 hover:bg-pink-50 cursor-pointer transition ${newLayoutTableShape === shape.id ? 'bg-pink-50 text-pink-600 font-bold' : 'text-gray-700 font-medium'}`}
                                            >
                                                <div className="w-8 flex justify-center">{shape.icon}</div>
                                                <span className="text-xs">{shape.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <button type="submit" className="bg-pink-600 text-white px-3 py-1.5 rounded-md text-xs font-bold hover:bg-pink-700 ml-1">Save</button>
                        <button type="button" onClick={() => setIsAddingLayout(false)} className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md text-xs font-bold hover:bg-gray-300">Cancel</button>
                    </form>
                ) : isEditingLayout ? (
                    <form onSubmit={handleUpdateLayout} className="flex flex-wrap items-center gap-2 bg-gray-50 p-1.5 rounded-lg border border-gray-200">
                        <input 
                            type="text" 
                            autoFocus
                            value={editLayoutName} 
                            onChange={e => setEditLayoutName(e.target.value)} 
                            className="w-32 p-1.5 border border-gray-300 rounded-md text-xs font-medium outline-none focus:ring-1 focus:ring-blue-500"
                            required
                        />
                        <button type="submit" className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-xs font-bold hover:bg-blue-700 ml-1">Update</button>
                        <button type="button" onClick={() => setIsEditingLayout(false)} className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md text-xs font-bold hover:bg-gray-300">Cancel</button>
                    </form>
                ) : (
                    <>
                        <select 
                            value={activeLayoutId} 
                            onChange={(e) => setActiveLayoutId(e.target.value)}
                            className="p-1.5 border border-gray-300 rounded-md text-sm font-medium outline-none focus:ring-1 focus:ring-pink-500"
                        >
                            {layouts.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                        <div className="flex gap-1 bg-gray-50 rounded-lg border border-gray-200 p-0.5">
                            <button onClick={handleEditLayoutClick} className="p-1.5 text-gray-500 hover:bg-white hover:text-blue-600 rounded transition" title="Rename Layout"><PlannerIcons.Document /></button>
                            <div className="w-px bg-gray-300 my-1"></div>
                            <button onClick={handleDeleteLayoutClick} className="p-1.5 text-gray-500 hover:bg-white hover:text-red-500 rounded transition" title="Delete Layout"><PlannerIcons.Trash /></button>
                        </div>
                        <button onClick={() => setIsAddingLayout(true)} className="p-1.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-white transition text-gray-600 hover:text-pink-600 font-bold text-sm ml-1">+ New</button>
                    </>
                )}
            </div>

            {isEditing ? (
                <form onSubmit={handleTableSubmit} className="bg-white p-4 border-b border-gray-200 flex flex-wrap gap-3 items-center">
                  <input 
                      type="text" 
                      placeholder="Table/Element Name" 
                      value={newTableName} 
                      onChange={e => setNewTableName(e.target.value)} 
                      className="w-32 p-2 border border-gray-300 rounded-md text-sm font-medium focus:outline-none focus:ring-1 focus:ring-pink-500" 
                      required 
                  />
                  
                  {!['custom', 'dance-floor', 'stage', 'bar', 'buffet', 'dj-booth'].includes(newTableShape) && (
                      <input 
                          type="number" 
                          value={newTableCapacity} 
                          onChange={e => setNewTableCapacity(parseInt(e.target.value))} 
                          min="1" max="24" 
                          className="w-16 p-2 border border-gray-300 rounded-md text-sm font-medium focus:outline-none focus:ring-1 focus:ring-pink-500" 
                          required title="Capacity" 
                      />
                  )}
                  
                  <div className="relative" ref={shapeDropdownRef}>
                      <button 
                          type="button" 
                          onClick={() => setShowShapeDropdown(!showShapeDropdown)}
                          className="w-36 p-2 border border-gray-300 rounded-md text-sm font-medium focus:outline-none focus:ring-1 focus:ring-pink-500 bg-white flex items-center justify-between"
                      >
                          <div className="flex items-center gap-2">
                              {tableShapes.find(s => s.id === newTableShape)?.icon}
                              <span className="truncate">{tableShapes.find(s => s.id === newTableShape)?.name}</span>
                          </div>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${showShapeDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {showShapeDropdown && (
                          <div className="absolute z-[100] top-full mt-1 left-0 w-44 bg-white border border-gray-200 rounded-lg shadow-xl py-1">
                              {tableShapes.map(shape => (
                                  <div 
                                      key={shape.id} 
                                      onClick={() => { setNewTableShape(shape.id); setShowShapeDropdown(false); }}
                                      className={`flex items-center gap-3 px-3 py-2 hover:bg-pink-50 cursor-pointer transition ${newTableShape === shape.id ? 'bg-pink-50 text-pink-600 font-bold' : 'text-gray-700 font-medium'}`}
                                  >
                                      <div className="w-8 flex justify-center">{shape.icon}</div>
                                      <span className="text-sm">{shape.name}</span>
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>

                  {newTableShape === 'custom' && (
                      <>
                          <select 
                              value={newCustomShapeType} 
                              onChange={e => setNewCustomShapeType(e.target.value)} 
                              className="w-24 p-2 border border-gray-300 rounded-md text-sm font-medium focus:outline-none focus:ring-1 focus:ring-pink-500"
                          >
                              <option value="rect">Rectangle</option>
                              <option value="circle">Circle</option>
                              <option value="pill">Pill</option>
                              <option value="square">Square</option>
                          </select>
                          <input 
                              type="color" 
                              value={newCustomColor} 
                              onChange={e => setNewCustomColor(e.target.value)} 
                              className="h-[38px] w-10 p-0.5 border border-gray-300 rounded cursor-pointer"
                              title="Element Color"
                          />
                      </>
                  )}
                  
                  <div className="flex items-center gap-1 border-l border-gray-200 pl-2 ml-1">
                      <label className="text-[10px] text-gray-500 font-bold">X:</label>
                      <input type="number" value={newXPos} onChange={e => setNewXPos(e.target.value)} className="w-12 p-1 border border-gray-300 rounded text-xs outline-none focus:ring-1 focus:ring-pink-500" title="X Position %" />
                      <label className="text-[10px] text-gray-500 font-bold ml-1">Y:</label>
                      <input type="number" value={newYPos} onChange={e => setNewYPos(e.target.value)} className="w-12 p-1 border border-gray-300 rounded text-xs outline-none focus:ring-1 focus:ring-pink-500" title="Y Position %" />
                  </div>
                  <div className="flex items-center gap-1 border-l border-gray-200 pl-2 ml-1">
                      <label className="text-[10px] text-gray-500 font-bold" title="Rotation Degrees">Rot°:</label>
                      <input type="number" value={newRotation} onChange={e => setNewRotation(e.target.value)} className="w-14 p-1 border border-gray-300 rounded text-xs outline-none focus:ring-1 focus:ring-pink-500" title="Rotation Degrees" />
                      <label className="text-[10px] text-gray-500 font-bold ml-1" title="Width in feet">W (ft):</label>
                      <input 
                          type="number" 
                          step="0.5" 
                          min="1" 
                          value={activeW} 
                          onChange={e => setNewScale(parseFloat(e.target.value) / activeBaseDim.w || 1)} 
                          className="w-14 p-1 border border-gray-300 rounded text-xs outline-none focus:ring-1 focus:ring-pink-500" 
                          title="Width in feet" 
                      />
                      <span className="text-[10px] text-gray-400 font-bold ml-0.5 whitespace-nowrap">x {activeH}'</span>
                  </div>

                  <button type="submit" className="bg-pink-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-pink-700 transition shadow-sm whitespace-nowrap">
                      {editingTableId ? 'Save' : 'Add'}
                  </button>
                  
                  {editingTableId && (
                      <button type="button" onClick={cancelEdit} className="text-gray-500 hover:text-gray-700 font-bold text-sm px-2 transition">Cancel</button>
                  )}
                  <button type="button" onClick={() => setIsEditing(false)} className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-md text-sm font-bold transition ml-auto">Done</button>
                </form>
            ) : (
                <div className="flex items-center gap-2 ml-auto w-full xl:w-auto justify-end flex-wrap">
                    <button onClick={handleAutoGenerateFloorPlanClick} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm hover:opacity-90 transition whitespace-nowrap flex items-center gap-1.5">
                        <span>✨</span> Auto-Generate
                    </button>
                    <button onClick={() => setIsEditing(true)} className="bg-gray-900 text-white px-5 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-black transition whitespace-nowrap">
                        ✏️ Edit Layout
                    </button>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end border-t xl:border-t-0 xl:border-l border-gray-200 pt-3 xl:pt-0 xl:pl-3 shrink-0">
                <button 
                    onClick={() => {
                        setIsEditing(!isEditing);
                        if (isEditing) cancelEdit();
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm font-bold transition flex items-center gap-1.5 shadow-sm ${isEditing ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                    {isEditing ? '✅ Done Editing' : '✏️ Edit Layout'}
                </button>

                {/* NEW: Undo/Redo Controls */}
                {viewMode === 'cad' && isEditing && (
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                        <button onClick={handleUndo} disabled={history.length === 0} className="p-1.5 px-3 rounded-md hover:bg-white disabled:opacity-30 transition text-gray-600" title="Undo">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                        </button>
                        <div className="w-px bg-gray-300 mx-1"></div>
                        <button onClick={handleRedo} disabled={redoStack.length === 0} className="p-1.5 px-3 rounded-md hover:bg-white disabled:opacity-30 transition text-gray-600" title="Redo">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" /></svg>
                        </button>
                    </div>
                )}
                
                {/* Real-World Room Dimensions Selector */}
                {viewMode === 'cad' && isEditing && (
                    <div className="flex items-center gap-1.5 bg-white border border-gray-200 p-1.5 rounded-lg shadow-sm">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1 hidden sm:inline">Room:</span>
                        <input 
                            type="number" 
                            value={roomDim.w} 
                            onChange={e => setRoomDim({...roomDim, w: parseInt(e.target.value) || 10})} 
                            className="w-12 p-1 border border-gray-300 rounded text-xs font-bold text-center outline-none focus:border-pink-500" 
                            title="Width (ft)" 
                        />
                        <span className="text-xs text-gray-500 font-bold">x</span>
                        <input 
                            type="number" 
                            value={roomDim.h} 
                            onChange={e => setRoomDim({...roomDim, h: parseInt(e.target.value) || 10})} 
                            className="w-12 p-1 border border-gray-300 rounded text-xs font-bold text-center outline-none focus:border-pink-500" 
                            title="Length (ft)" 
                        />
                        <span className="text-[10px] font-bold text-gray-500 pr-1">FT</span>
                        <button 
                            onClick={() => updateProject(project.id, { roomDimensions: roomDim })} 
                            className="bg-gray-900 text-white px-2 py-1 rounded text-xs font-bold hover:bg-black transition"
                        >
                            Set
                        </button>
                    </div>
                )}
                
                <div className="flex bg-gray-100 p-1 rounded-lg">
                    {/* NEW: Smart Auto-Seat Button */}
                    <button onClick={handleAutoSeatGuests} disabled={isAutoSeating} className="px-3 py-1.5 rounded-md text-sm font-bold text-purple-700 bg-purple-100 hover:bg-purple-200 transition mr-1 flex items-center gap-1 shadow-sm whitespace-nowrap">
                        {isAutoSeating ? '⏳ Seating...' : '🪄 Auto-Seat'}
                    </button>
                    <button onClick={() => setViewMode('cad')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${viewMode === 'cad' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>CAD View</button>
                    <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>List View</button>
                </div>
                
                <button onClick={toggleFullscreen} className={`w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-md transition border ${isFullscreen ? 'bg-pink-100 text-pink-700 border-pink-200 hover:bg-pink-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 shadow-sm'}`} title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {isFullscreen ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                        )}
                    </svg>
                </button>
            </div>
        </div>

        {viewMode === 'list' ? (
            <div className="flex-1 overflow-y-auto pb-6 pr-2 custom-scrollbar min-h-[400px] lg:min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-1">
                {tables.map(table => {
                  const isCustom = table.shape === 'custom';
                  const seatedGuests = guests.filter(g => g.tableId === table.id);
                  const isFull = !isCustom && seatedGuests.length >= table.capacity;
                  
                  return (
                    <div 
                      key={table.id} 
                      onClick={() => {
                          if (selectedGuestId && !isCustom && !isFull) {
                              assignGuest(selectedGuestId, table.id);
                              setSelectedGuestId(null);
                          }
                      }}
                      className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-auto max-h-[300px] transition-all ${
                          !isCustom ? 'hover:ring-2 hover:ring-pink-300 cursor-pointer' : ''
                      } ${selectedGuestId && !isCustom && !isFull ? 'ring-2 ring-pink-500 bg-pink-50/30' : ''}`}
                      onDragOver={(e) => { if (!isCustom) e.preventDefault(); }}
                      onDrop={(e) => { if (!isCustom) handleTableDrop(e, table.id); }}
                    >
                      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center sticky top-0 z-10">
                        <div className="flex flex-wrap items-center gap-2 md:gap-3">
                          <h4 className="font-bold text-gray-900 truncate max-w-[120px]">{table.name}</h4>
                          {isCustom ? (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap bg-gray-800 text-white">Custom Layout Element</span>
                          ) : (
                              <span className={`text-[10px] font-bold px-2 py-0.5 md:px-2.5 md:py-1 rounded-full whitespace-nowrap ${isFull ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                {seatedGuests.length} / {table.capacity} Seated
                              </span>
                          )}
                        </div>
                        {isEditing && (
                            <div className="flex gap-1">
                                <button onClick={() => handleEditTableClick(table)} className="text-gray-400 hover:text-blue-500 transition-colors p-1" title="Edit Table">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                </button>
                                <button onClick={() => removeTable(table.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1" title="Delete Table"><PlannerIcons.Trash /></button>
                            </div>
                        )}
                      </div>
                      <div className="p-2 flex-1 overflow-y-auto">
                        {isCustom ? (
                            <div className="text-center py-6 text-gray-400 text-xs md:text-sm italic">Not applicable for seating.</div>
                        ) : seatedGuests.length === 0 ? (
                            <div className="text-center py-6 text-gray-400 text-xs md:text-sm italic">This table is empty. Drag guests here!</div>
                        ) : (
                            <div className="space-y-1">
                              {seatedGuests.map((guest, i) => (
                                <div 
                                  key={guest.id} 
                                  draggable="true"
                                  onDragStart={(e) => handleGuestDragStart(e, guest.id)}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      if (selectedGuestId === guest.id) setSelectedGuestId(null);
                                      else setSelectedGuestId(guest.id);
                                  }}
                                  className={`flex items-center justify-between p-2 hover:bg-gray-50 border rounded-lg group text-sm transition-colors cursor-pointer ${
                                      selectedGuestId === guest.id ? 'bg-pink-50 border-pink-300 ring-1 ring-pink-500' : 'border-transparent hover:border-gray-100'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 min-w-0 pr-2">
                                    <span className="text-gray-400 font-mono text-[10px] md:text-xs w-4 md:w-5 text-right flex-shrink-0">{i + 1}.</span>
                                    <span className="font-bold text-gray-800 truncate">{guest.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {guest.meal && <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded font-bold hidden sm:inline-block">{guest.meal}</span>}
                                    <button onClick={() => assignGuest(guest.id, null)} className="text-[10px] md:text-[11px] text-red-500 font-bold hover:underline bg-red-50 px-2 py-1 rounded">Remove</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
        ) : (
            <div 
                ref={outerRef}
                className="flex-1 relative w-full bg-[#f8f9fa] rounded-xl border-2 border-gray-200 overflow-hidden touch-none min-h-[400px] lg:min-h-0 mb-8 lg:mb-12"
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                onTouchMove={handleCanvasMouseMove}
                onTouchEnd={handleCanvasMouseUp}
            >
                {/* NEW: Helper Note for CAD Drag & Drop */}
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-30 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-sm border border-pink-200 text-xs font-bold text-pink-700 pointer-events-none text-center animate-fade-in hidden sm:block">
                    💡 Tip: Hover over tables to drag & drop guests!
                </div>

                {/* Floating Canvas Controls */}
                <div className="absolute bottom-6 right-6 z-30 flex flex-col gap-1 bg-white/90 backdrop-blur-md rounded-xl shadow-lg border border-gray-200 p-1.5">
                    {/* NEW: Tool Selectors */}
                    <button onClick={() => { setActiveTool('select'); setMeasureStart(null); setMeasureEnd(null); }} className={`p-2 rounded-lg transition ${activeTool === 'select' ? 'bg-pink-100 text-pink-600' : 'hover:bg-pink-50 text-gray-700'}`} title="Select / Pan Tool">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>
                    </button>
                    <button onClick={() => setActiveTool('measure')} className={`p-2 rounded-lg transition ${activeTool === 'measure' ? 'bg-pink-100 text-pink-600' : 'hover:bg-pink-50 text-gray-700'}`} title="Tape Measure">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 10h16v4H4z M6 10v2 M10 10v2 M14 10v2 M18 10v2" /></svg>
                    </button>
                    <div className="w-full h-px bg-gray-200 my-0.5"></div>
                    <button onClick={() => setSnapToGrid(!snapToGrid)} className={`p-2 rounded-lg transition ${snapToGrid ? 'bg-blue-100 text-blue-600' : 'hover:bg-blue-50 text-gray-700'}`} title="Snap to Grid">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                    </button>
                    <div className="w-full h-px bg-gray-200 my-0.5"></div>
                    <button onClick={handleZoomIn} className="p-2 hover:bg-pink-50 hover:text-pink-600 text-gray-700 rounded-lg transition" title="Zoom In">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" /></svg>
                    </button>
                    <div className="w-full h-px bg-gray-200 my-0.5"></div>
                    <button onClick={handleZoomOut} className="p-2 hover:bg-pink-50 hover:text-pink-600 text-gray-700 rounded-lg transition" title="Zoom Out">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" /></svg>
                    </button>
                    <div className="w-full h-px bg-gray-200 my-0.5"></div>
                    <button onClick={handleFit} className="p-2 hover:bg-pink-50 hover:text-pink-600 text-gray-700 rounded-lg transition" title="Fit to Space">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                    </button>
                </div>

                {/* Inner Scalable/Pannable Canvas Area */}
                <div
                    id="canvas-bg"
                    ref={canvasRef}
                    className={`absolute bg-white shadow-inner ${activeTool === 'measure' ? 'cursor-crosshair' : 'cursor-grab active:cursor-grabbing'}`}
                    style={{
                        width: `${canvasW}px`,
                        height: `${canvasH}px`,
                        left: '50%',
                        top: '50%',
                        marginLeft: `-${canvasW / 2}px`,
                        marginTop: `-${canvasH / 2}px`,
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: '50% 50%',
                        backgroundImage: `radial-gradient(#cbd5e1 1.5px, transparent 1.5px)`,
                        backgroundSize: `${PX_PER_FT}px ${PX_PER_FT}px`,
                        backgroundPosition: "center center",
                        transition: isPanning ? 'none' : 'transform 0.1s ease-out'
                    }}
                    onMouseDown={handleCanvasMouseDownBg}
                    onTouchStart={handleCanvasMouseDownBg}
                >
                    {/* Visual Guidelines render when dragging */}
                    {guides.x !== null && <div className="absolute top-0 bottom-0 w-px bg-pink-500 z-0 pointer-events-none opacity-50" style={{ left: `${guides.x}%` }} />}
                    {guides.y !== null && <div className="absolute left-0 right-0 h-px bg-pink-500 z-0 pointer-events-none opacity-50" style={{ top: `${guides.y}%` }} />}

                    {/* NEW: Measurement Line Overlay */}
                    {measureStart && measureEnd && (
                        <svg className="absolute inset-0 w-full h-full pointer-events-none z-[100]" style={{ overflow: 'visible' }}>
                            <line 
                                x1={`${measureStart.x}%`} 
                                y1={`${measureStart.y}%`} 
                                x2={`${measureEnd.x}%`} 
                                y2={`${measureEnd.y}%`} 
                                stroke="#ec4899" 
                                strokeWidth={4 / zoom} 
                                strokeDasharray={`${8 / zoom}, ${8 / zoom}`}
                            />
                            <circle cx={`${measureStart.x}%`} cy={`${measureStart.y}%`} r={6 / zoom} fill="#ec4899" />
                            <circle cx={`${measureEnd.x}%`} cy={`${measureEnd.y}%`} r={6 / zoom} fill="#ec4899" />
                            <rect 
                                x={`calc(${(measureStart.x + measureEnd.x) / 2}% - ${30 / zoom}px)`}
                                y={`calc(${(measureStart.y + measureEnd.y) / 2}% - ${18 / zoom}px)`}
                                width={`${60 / zoom}px`}
                                height={`${24 / zoom}px`}
                                rx={`${4 / zoom}px`}
                                fill="white"
                                opacity="0.8"
                            />
                            <text 
                                x={`${(measureStart.x + measureEnd.x) / 2}%`} 
                                y={`${(measureStart.y + measureEnd.y) / 2}%`} 
                                fill="#ec4899"
                                fontSize={`${14 / zoom}px`}
                                fontWeight="bold"
                                textAnchor="middle"
                                alignmentBaseline="central"
                            >
                                {Math.round(Math.sqrt(
                                    Math.pow((measureEnd.x - measureStart.x) * (roomDim.w / 100), 2) + 
                                    Math.pow((measureEnd.y - measureStart.y) * (roomDim.h / 100), 2)
                                ) * 10) / 10} ft
                            </text>
                        </svg>
                    )}

                    {localTables.map(table => {
                        const isCustom = table.shape === 'custom';
                        const seatedGuests = guests.filter(g => g.tableId === table.id);
                        const isFull = !isCustom && seatedGuests.length >= table.capacity;
                        const isEditingThisTable = editingTableId === table.id;
                        
                        const baseDim = { ...(shapeDimensions[table.shape] || shapeDimensions['round']) };
                        if (isCustom && table.customShapeType === 'pill') {
                            baseDim.w = 8;
                            baseDim.h = 3;
                        }
                        
                        const actualW = baseDim.w * (table.scale || 1);
                        const actualH = baseDim.h * (table.scale || 1);
                        const pxW = actualW * PX_PER_FT;
                        const pxH = actualH * PX_PER_FT;

                        let shapeClass = '';
                        let dynamicStyle = { 
                            left: `${table.x}%`, 
                            top: `${table.y}%`,
                            width: `${pxW}px`,
                            height: `${pxH}px`,
                            transform: `translate(-50%, -50%) rotate(${table.rotation || 0}deg) ${draggingTable === table.id || isEditingThisTable ? 'scale(1.05)' : ''}`
                        };
                        
                        if (isCustom) {
                            shapeClass = 'shadow-xl text-white border-2 border-black/20';
                            dynamicStyle.backgroundColor = table.color || '#1f2937';
                            if (table.customShapeType === 'circle' || table.customShapeType === 'pill') shapeClass += ' rounded-full';
                            else shapeClass += ' rounded-md';
                        }
                        else {
                            if (table.shape === 'round' || table.shape === 'cocktail') shapeClass += ' rounded-full';
                            else if (table.shape === 'oval') shapeClass += ' rounded-[50%]';
                            else if (table.shape === 'sweetheart' || table.shape === 'half-moon') shapeClass += ' rounded-t-full rounded-b-md';
                            else shapeClass += ' rounded-md';
                            
                            if (table.shape === 'dance-floor') shapeClass += ' border-2 !border-gray-300 !bg-gray-100/80 !text-gray-400 font-light tracking-widest text-base md:text-xl';
                            else if (table.shape === 'stage') shapeClass += ' border-2 !border-amber-300 !bg-amber-50 !text-amber-800 text-sm md:text-lg font-bold';
                            else if (table.shape === 'bar') shapeClass += ' !bg-gray-800 !text-white !border-gray-900';
                            else if (table.shape === 'buffet') shapeClass += ' !bg-gray-100 border-2 !border-gray-300 !text-gray-600';
                            else if (table.shape === 'dj-booth') shapeClass += ' !bg-gray-900 !text-white !border-black';
                        }

                        const dimText = (table.shape === 'round' || table.shape === 'cocktail') 
                            ? `${Math.round(actualW*10)/10}' Ø` 
                            : `${Math.round(actualW*10)/10}' x ${Math.round(actualH*10)/10}'`;
                        
                        return (
                            <div
                                key={table.id}
                                className={`absolute flex flex-col items-center justify-center text-center shadow-lg border-[3px] transition-colors group select-none ${shapeClass} ${!isCustom ? (isFull ? 'bg-red-50 border-red-300' : 'bg-white border-pink-300 hover:border-pink-400') : ''} ${draggingTable === table.id ? 'opacity-80 z-[60] ring-4 ring-pink-400/30' : isEditingThisTable ? 'z-[70] ring-4 ring-blue-500 shadow-blue-500/30 border-blue-500' : 'z-10 hover:z-50'} ${isEditing ? 'cursor-grab active:cursor-grabbing' : (selectedGuestId && !isCustom && !isFull ? 'cursor-pointer ring-4 ring-pink-500 shadow-pink-500/50 animate-pulse' : 'cursor-pointer')}`}
                                style={dynamicStyle}
                                onMouseDown={(e) => isEditing && handleTableDragStart(e, table.id)}
                                onTouchStart={(e) => isEditing && handleTableDragStart(e, table.id)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isEditing) return;
                                    if (selectedGuestId && !isCustom && !isFull) {
                                        assignGuest(selectedGuestId, table.id);
                                        setSelectedGuestId(null);
                                    } else {
                                        setActiveTableTooltip(activeTableTooltip === table.id ? null : table.id);
                                    }
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => !isCustom && handleTableDrop(e, table.id)}
                            >
                                <span className={`font-bold text-xs truncate w-full px-1 pointer-events-none ${isCustom || table.shape==='bar' || table.shape==='dj-booth' ? 'text-white' : 'text-gray-900'}`}>{table.name}</span>
                                
                                <span className={`text-[8px] md:text-[10px] font-medium opacity-70 px-1 pointer-events-none -mt-0.5 ${isCustom || table.shape==='bar' || table.shape==='dj-booth' ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {dimText}
                                </span>
                                
                                {/* REFINED: Interactive Tooltip for Drag and Quick Remove */}
                                <div 
                                    className={`absolute top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-2xl transition-opacity z-50 p-3 text-left cursor-default ${activeTableTooltip === table.id ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto'}`}
                                    style={{ transform: `scale(${1 / zoom}) rotate(-${table.rotation || 0}deg)`, transformOrigin: 'top center' }}
                                    onMouseDown={(e) => e.stopPropagation()} 
                                    onTouchStart={(e) => e.stopPropagation()}
                                >
                                    <p className="text-sm font-black text-gray-900 border-b border-gray-100 pb-1 mb-2 truncate">{table.name}</p>
                                    {!isCustom ? (
                                        <>
                                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">Seated Guests ({seatedGuests.length}/{table.capacity})</p>
                                            {seatedGuests.length > 0 ? (
                                                <ul className="text-xs text-gray-700 space-y-1">
                                                    {seatedGuests.map(g => (
                                                        <li 
                                                          key={g.id} 
                                                          draggable="true"
                                                          onDragStart={(e) => handleGuestDragStart(e, g.id)}
                                                          onClick={(e) => {
                                                              e.stopPropagation();
                                                              if (selectedGuestId === g.id) setSelectedGuestId(null);
                                                              else setSelectedGuestId(g.id);
                                                          }}
                                                          className={`flex justify-between items-center cursor-pointer p-1 rounded transition-colors group/guest ${
                                                              selectedGuestId === g.id ? 'bg-pink-100 ring-1 ring-pink-400' : 'hover:bg-gray-50'
                                                          }`}
                                                        >
                                                            <span className="truncate flex-1 font-medium">• {g.name}</span>
                                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                                {g.meal && <span className="text-[9px] bg-gray-100 px-1.5 py-0.5 rounded font-bold text-gray-600">{g.meal}</span>}
                                                                <button onClick={(e) => { e.stopPropagation(); assignGuest(g.id, null); }} className="text-red-400 hover:text-red-600 font-bold ml-1 opacity-0 group-hover/guest:opacity-100 transition-opacity p-0.5" title="Unseat">×</button>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-[10px] text-gray-400 italic">No guests seated here.</p>
                                            )}
                                        </>
                                    ) : (
                                        <p className="text-xs text-gray-500">Custom Layout Element</p>
                                    )}
                                </div>

                                {!isCustom && (
                                    <>
                                        <span className="text-[10px] text-gray-600 font-medium pointer-events-none bg-white/50 px-1 rounded mt-0.5">{seatedGuests.length}/{table.capacity}</span>
                                        
                                        <div className="absolute inset-0 pointer-events-none flex flex-wrap items-center justify-center gap-1 p-2 opacity-30">
                                            {Array.from({ length: table.capacity }).map((_, i) => (
                                                <div key={i} className={`w-2 h-2 rounded-full ${i < seatedGuests.length ? 'bg-pink-600' : 'bg-gray-400'}`} />
                                            ))}
                                        </div>
                                    </>
                                )}

                                {/* NEW: Scale Handle */}
                                {isEditing && (isEditingThisTable || activeTableTooltip === table.id) && (
                                    <div 
                                        className="absolute -bottom-3 -right-3 w-6 h-6 bg-white border-2 border-pink-500 rounded-full cursor-nwse-resize z-40 flex items-center justify-center hover:scale-110 shadow-lg"
                                        onMouseDown={(e) => handleScaleStart(e, table)}
                                        onTouchStart={(e) => handleScaleStart(e, table)}
                                        style={{ transform: `scale(${1 / zoom}) rotate(-${table.rotation || 0}deg)`, transformOrigin: 'center' }}
                                        title="Drag to scale"
                                    >
                                        <svg className="w-3 h-3 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l5-5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                                    </div>
                                )}

                                {/* Hover Actions */}
                                {isEditing && (
                                    <div 
                                        className="absolute -top-12 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-50 bg-white/95 backdrop-blur shadow-xl p-1.5 rounded-full border border-gray-200 pointer-events-auto"
                                        style={{ transform: `scale(${1 / zoom}) rotate(-${table.rotation || 0}deg)`, transformOrigin: 'bottom center' }}
                                    >
                                        <button onClick={(e) => { e.stopPropagation(); bringToFront(table.id); }} className="text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-full p-1.5 transition" title="Bring to Front">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" /></svg>
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); quickRotate(table); }} className="text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-full p-1.5 transition" title="Rotate 45°">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); duplicateTable(table); }} className="text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-full p-1.5 transition" title="Duplicate">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                        </button>
                                        <div className="w-px bg-gray-300 mx-0.5 my-1"></div>
                                        <button onClick={(e) => { e.stopPropagation(); handleEditTableClick(table); }} className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full p-1.5 transition" title="Edit Properties">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); removeTable(table.id); }} className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full p-1.5 transition" title="Delete">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        )}
      </div>
    </>
  );

  if (isFullscreen && typeof document !== 'undefined') {
      return createPortal(
          <div className="fixed inset-0 z-[9999] bg-gray-100 flex flex-col lg:flex-row gap-2 lg:gap-4 p-2 sm:p-4 md:p-6 pb-12 md:pb-16 animate-fade-in w-full h-[100dvh] overflow-y-auto overflow-x-hidden">
              {seatingContent}
          </div>,
          document.body
      );
  }

  return (
      <div className="flex flex-col lg:flex-row gap-6 h-auto lg:h-[calc(100vh-250px)] relative z-10 min-h-0 pb-12 md:pb-16">
          {seatingContent}
      </div>
  );
}
